import { Component, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';

@Component({
  selector: 'app-smart-integration',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './smart-integration.component.html',
  styleUrl: './smart-integration.component.scss'
})
export class SmartIntegrationComponent implements OnInit, OnDestroy {
  private jsonLdScript: any;
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  nav(url: any) {
    console.log(url);
    this.router.navigateByUrl(url)
  }
  ngOnInit(): void {
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const healthcareIntegrationSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/smart-integration",
          "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/smart-integration",
          "name": "Smart Healthcare Integration Engine for Seamless Enterprise Connectivity – Case Study",
          "description": "Case study on how Kalpita Technologies designed and built ARC, a centralized healthcare integration engine that unified 30+ internal and third-party applications.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en",
          "datePublished": "2025-01-28"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/smart-integration#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/case-study", "name": "Case Studies" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/smart-integration", "name": "Healthcare Integration" } }
          ]
        },
        {
          "@type": "Article",
          "headline": "Smart Healthcare Integration Engine for Seamless Enterprise Connectivity",
          "articleBody": "Kalpita Technologies partnered with a leading healthcare technology service provider to design and implement a smart integration engine called ARC that centralized workflows across internal and external systems...",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2025-01-28",
          "image": "https://www.kalpitatechnologies.com/assets/industry/casestudies/integration-computerHealthcare.png"
        },
        {
          "@type": "HowTo",
          "name": "How to Build a Centralized Healthcare Integration Engine",
          "step": [
            { "@type": "HowToStep", "name": "Assess Landscape", "text": "Audit all internal and third-party applications to identify silos and governance gaps." },
            { "@type": "HowToStep", "name": "Define Architecture", "text": "Design a centralized engine where any system can act as both source and destination." },
            { "@type": "HowToStep", "name": "Implement SSO", "text": "Set up Single Sign-On using Office 365 Azure AD Authentication." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/smart-integration#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What results did the ARC integration platform achieve?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The platform centralized 30+ applications, reduced operational complexity, and improved system interoperability and security through SSO."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(healthcareIntegrationSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
}
